import moment from "moment";
import queryString from "query-string-for-all";
import { composeInsights } from "../redux/modules/insights/utils";

/**
 * FYI: Mixpanel expects date type as ISOString
 */
export const parseDateToISOString = (dateInput) => {
  if (dateInput === null || dateInput === undefined || dateInput === "") {
    return null;
  }
  const dateToParse =
    typeof dateInput === "number" ? new Date(dateInput * 1000) : dateInput;
  return moment(dateToParse).toISOString();
};

/** Parse action to eliminate duplication of code */
const parseAction = (action) => ({
  actionKey: action.key || null,
  actionSummary: action.summary || null,
  siteName: action.components || null,
  actionPriority: action.priority || null,
  actionResponsible: action.responsible || null,
  actionContext: action.context || null,
  actionStatus: action.bonfire_status || null,
});

/** Event: When user view a rule in Insights page */
export const parseRuleViewedEventPayload = (ruleGroup) => {
  const rule = composeInsights({ 0: ruleGroup })[0];
  return {
    ruleId: rule.ruleId,
    ruleName: rule.ruleName,
    siteName: rule.site.label,
    severity: rule.severity,
    totalDuration: rule.duration,
    snoozedDuration: rule.duration - rule.activeDuration,
    activeDuration: rule.activeDuration,
    ruleType: rule.service,
    actionsCount: rule.actionsCount,
    totalHits: rule.hits,
    activeHits: rule.activeHits,
    snoozedHits: rule.hits - rule.activeHits,
    totalEquips: rule.targetCount,
    activeEquips: rule.activeTargetCount,
    snoozedEquips: rule.targetCount - rule.activeTargetCount,
    latest: rule.latestLabel,
  };
};

/** Event: When user view a report in Insights page by clicking reportURL for SS */
export const parseReportViewedEventPayload = (
  asset,
  ruleName,
  isAssociated
) => ({
  ruleId: asset.ruleId,
  ruleName,
  siteName: asset.site.label,
  ruleType: asset.ruleType,
  equipName: asset.equipName || null,
  equipDescription: asset.description,
  numberOfHits: asset.times.length,
  totalDuration: asset.duration,
  averageDuration: asset.averageDur,
  associatedActionKey: asset.issueKey,
  isSnoozed: asset.isSnoozed,
  snoozeEnd: parseDateToISOString(asset.snoozeEnd),
  isAssociated,
  reportURL: asset.reportURL,
});

/** Event: When user post a comment under actions, an event triggered to track interactions */
export const parsePostCommentEventPayload = (issue, commentResult) => {
  let equips = [];
  if (Array.isArray(issue.equipRules) && issue.equipRules.length > 0) {
    equips = issue.equipRules.map((equipRule) => ({
      targetName: equipRule.target.name,
      ruleName: equipRule.rule.name,
      ruleType: equipRule.rule.type,
      severity: equipRule.rule.severity,
      hitOccurredOn: parseDateToISOString(equipRule.hit.occurredOn),
    }));
  }
  return {
    actionCreatedOn: parseDateToISOString(issue.created),
    equipRules: equips,
    comment:
      commentResult.result && commentResult.result.body
        ? commentResult.result.body
        : null,
    ...parseAction(issue),
  };
};

/** Event: When user save changes for an action, a event triggered to track interactions */
export const parseActionSavedEventPayload = (
  action,
  actionId,
  updatedAction
) => ({
  actionKey: actionId,
  actionContext: action.context.value,
  actionSummary: action.summary,
  actionStatus: action.status.value,
  actionPriority: action.priority.value,
  actionResponsible: action.responsible.value,
  actionCreatedOn: parseDateToISOString(updatedAction.created),
  siteName: updatedAction.components,
  ruleName: updatedAction.rule,
});

/**
 * helper method used to minimise duplicate mapping
 */
const parseRule = (rule) => ({
  ruleId: rule.id,
  ruleName: rule.name,
  ruleType: rule.type,
  severity: rule.severity,
});

/** Event: When user snooze and equip rule by clicking snooze icon in Insights page */
export const parseSnoozeEquipRuleEventPayload = (
  siteName,
  duration,
  equipRuleId,
  successResult
) => ({
  hitOccurredOn: parseDateToISOString(successResult.hit.occurredOn),
  associatedActionKey: successResult.issueKey,
  equipName: successResult.target.name,
  snoozeEnd: parseDateToISOString(successResult.snoozeEnd),
  siteName,
  equipRuleId,
  duration,
  ...parseRule(successResult.rule),
});

/**
 * Parse function used to parse associate result for both Added Action event and associate action event
 */
export const parseAssociateSuccessResult = (successResult) =>
  successResult === null || successResult === undefined
    ? []
    : successResult.map((equipRule) => ({
        equipRuleId: equipRule.id,
        actionKey: equipRule.issueKey,
        snoozeEnd: equipRule.snoozeEnd,
        targetId: equipRule.target.id,
        targetName: equipRule.target.name,
        ...parseRule(equipRule.rule),
      }));

/** Event: When user Add/Create an action, a event triggered to track interactions */
export const parseAddedActionEventPayload = (
  issueKey,
  issue,
  site = null,
  associateResult = null,
  associateError = null
) => ({
  actionContext: issue.issueContext,
  actionKey: issueKey,
  actionPriority: issue.priority,
  actionResponsible: issue.responsible,
  actionStatus: issue.status.value,
  actionSummary: issue.summary,
  equipRuleIds: issue.target !== "" ? issue.target : null,
  ruleName: issue.sparkType,
  siteName: site ? site.label : issue.components[0].name,
  equipRules: parseAssociateSuccessResult(associateResult),
  associateError: associateError || null,
});

export const parseActionForAssociateEvent = (betaIssues, successResult) => {
  const issueKeys = new Set(
    successResult.map((equipRule) => equipRule.issueKey)
  );
  const issueKey = [...issueKeys.values()][0];
  const issue =
    (betaIssues.length > 0
      ? betaIssues.find((action) => action.key === issueKey)
      : {}) || {};
  return {
    ...parseAction(issue),
  };
};

/** Event: When user associate an equip rule to an existing issue in Insights */
export const parseAssociateInsightWithActionEventPayload = (
  betaIssues,
  successResult
) => ({
  equipRules: parseAssociateSuccessResult(successResult),
  ...parseActionForAssociateEvent(betaIssues, successResult),
});

export const stripSitesQuery = (search) => {
  const qs = queryString.parse(search);
  return {
    ...qs,
    sites: undefined,
  };
};
